import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Backdrop,
  CircularProgress,
  Box,
  Pagination,
  Typography,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import moment from "moment";
import { toast } from "react-toastify";
import OurLayouts from "../Layouts";
import apiService from "../../services/apiservice";

const ITEMS_PER_PAGE = 10;
const RESPONSE_CODE_SUCCESS = "200";
const EXPORT_FILE_NAME_FORMAT = "YYYY-MM-DD";
const DATE_FORMAT = "DD-MM-YYYY";

const TAB_INDICES = {
  HOSPITAL: 0,
  EXECUTIVE: 1,
};

const FeedbackReport = () => {
  const ROLE_CODE_CE = "CE";

  const [activeTab, setActiveTab] = useState(TAB_INDICES.HOSPITAL);
  const activeTabRef = useRef(TAB_INDICES.HOSPITAL);

  const [loading, setLoading] = useState(false);

  const [hospitalCurrentPage, setHospitalCurrentPage] = useState(1);
  const [executiveCurrentPage, setExecutiveCurrentPage] = useState(1);

  const hospitalCurrentPageRef = useRef(1);
  const executiveCurrentPageRef = useRef(1);

  const [totalRecords, setTotalRecords] = useState(0);
  const [dataList, setDataList] = useState([]);

  const [hospitalList, setHospitalList] = useState([]);
  const [executiveList, setExecutiveList] = useState([]);

  const [filterObj, setFilterObj] = useState({
    fromDate: moment().subtract(30, 'days').toDate(),
    toDate: moment().toDate(),
    hospitalCode: null,
    executiveCode: null,
  });

  const filterObjRef = useRef(filterObj);

  useEffect(() => {
    hospitalCurrentPageRef.current = hospitalCurrentPage;
  }, [hospitalCurrentPage]);

  useEffect(() => {
    executiveCurrentPageRef.current = executiveCurrentPage;
  }, [executiveCurrentPage]);

  useEffect(() => {
    filterObjRef.current = filterObj;
  }, [filterObj]);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  const fetchHospitalList = useCallback(async () => {
    try {
      const response = await apiService.getHospitalList();
      if (response?.data?.responseCode === RESPONSE_CODE_SUCCESS) {
        const hospitals = response.data.successObject || [];
        setHospitalList(hospitals.map(h => ({
          label: h.hospitalName,
          value: h.hospitalCode,
          ...h
        })));
      }
    } catch (error) {
      console.error("Error fetching hospital list:", error);
      toast.error("Failed to load hospital list");
    }
  }, []);

  const fetchExecutiveList = useCallback(async () => {
    try {
      const payload = {
        roleCode: ROLE_CODE_CE,
        status: 1
      };
      const res = await apiService.getUserList(payload);

      if (res.data?.responseCode === RESPONSE_CODE_SUCCESS) {
        const users = res.data.successList || res.data.data || [];
        const formattedUsers = users.map(user => ({
          value: user.code,
          label: user.userName
        }));
        setExecutiveList(formattedUsers);
      }
    } catch (error) {
      console.error("Error fetching concierge users:", error);
      toast.error("Failed to load user list");
    }
  }, []);

  useEffect(() => {
    fetchHospitalList();
    fetchExecutiveList();
  }, [fetchHospitalList, fetchExecutiveList]);

  const formatDate = useCallback((date, format) => {
    if (!date) return null;
    return moment(date).format(format || "YYYY-MM-DD");
  }, []);

  const cleanPayload = useCallback((payload) => {
    const cleaned = { ...payload };
    Object.keys(cleaned).forEach(key => {
      if (cleaned[key] === null || cleaned[key] === "" || cleaned[key] === undefined) {
        delete cleaned[key];
      }
    });
    return cleaned;
  }, []);

  // Issue 4 fix: use Number.parseInt instead of parseInt
  const renderStars = (rating) => {
    const numRating = Number.parseInt(rating) || 0;
    return (
      <Box display="flex" alignItems="center">
        {[1, 2, 3, 4, 5].map(star =>
          star <= numRating
            ? <StarIcon key={star} sx={{ color: "#FFD700", fontSize: "18px" }} />
            : <StarBorderIcon key={star} sx={{ color: "#ccc", fontSize: "18px" }} />
        )}
        <Typography ml={1} fontSize="12px" color="text.secondary">({numRating}/5)</Typography>
      </Box>
    );
  };

  const buildPayload = useCallback((filter, tab, pageNumber = 1) => {
    const isHospital = tab === TAB_INDICES.HOSPITAL;

    const basePayload = {
      pageNumber: pageNumber,
      pageSize: ITEMS_PER_PAGE,
      fromDate: filter.fromDate ? formatDate(filter.fromDate, "YYYY-MM-DD") : moment().subtract(30, 'days').format("YYYY-MM-DD"),
      toDate: filter.toDate ? formatDate(filter.toDate, "YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
    };

    if (isHospital) {
      return cleanPayload({
        ...basePayload,
        hospitalCode: filter.hospitalCode,
      });
    } else {
      return cleanPayload({
        ...basePayload,
        conciergeUserCode: filter.executiveCode,
      });
    }
  }, [formatDate, cleanPayload]);

  const getFeedbackList = useCallback(async () => {
    setLoading(true);
    try {
      const currentFilter = filterObjRef.current;
      const currentActiveTab = activeTabRef.current;

      const pageNumber = currentActiveTab === TAB_INDICES.HOSPITAL
        ? hospitalCurrentPageRef.current
        : executiveCurrentPageRef.current;

      const payload = buildPayload(currentFilter, currentActiveTab, pageNumber);

      const response = await apiService.geFeedbackList(payload);

      if (response?.data?.responseCode === RESPONSE_CODE_SUCCESS) {
        const data = response.data.successList ||
          response.data.data?.content ||
          response.data.content ||
          [];

        const total = response.data.pagination?.totalRecords ||
          response.data.data?.totalElements ||
          response.data.totalElements ||
          data.length;

        setDataList(data);
        setTotalRecords(total);
      } else {
        setDataList([]);
        setTotalRecords(0);
        if (response?.data?.responseMessage) {
          toast.info(response.data.responseMessage);
        }
      }
    } catch (err) {
      console.error("Error fetching feedback:", err);
      toast.error("Failed to load feedback data");
      setDataList([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [buildPayload]);

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
    setDataList([]);

    if (newValue === TAB_INDICES.HOSPITAL) {
      setHospitalCurrentPage(1);
    } else {
      setExecutiveCurrentPage(1);
    }

    setFilterObj(prev => ({
      ...prev,
      hospitalCode: newValue === TAB_INDICES.HOSPITAL ? prev.hospitalCode : null,
      executiveCode: newValue === TAB_INDICES.EXECUTIVE ? prev.executiveCode : null,
    }));

    setTimeout(() => {
      getFeedbackList();
    }, 0);
  }, [getFeedbackList]);

  const handleDateChange = useCallback((e, field) => {
    setFilterObj(prev => ({
      ...prev,
      [field]: e.value
    }));
  }, []);

  const handleSearch = useCallback(() => {
    setDataList([]);

    if (activeTabRef.current === TAB_INDICES.HOSPITAL) {
      setHospitalCurrentPage(1);
    } else {
      setExecutiveCurrentPage(1);
    }

    setTimeout(() => {
      getFeedbackList();
    }, 0);
  }, [getFeedbackList]);

  const handleReset = useCallback(() => {
    setFilterObj({
      fromDate: moment().subtract(30, 'days').toDate(),
      toDate: moment().toDate(),
      hospitalCode: null,
      executiveCode: null,
    });
    setDataList([]);

    if (activeTabRef.current === TAB_INDICES.HOSPITAL) {
      setHospitalCurrentPage(1);
    } else {
      setExecutiveCurrentPage(1);
    }

    setTimeout(() => {
      getFeedbackList();
    }, 0);
  }, [getFeedbackList]);

  const handleHospitalPageChange = useCallback((event, value) => {
    setHospitalCurrentPage(value);
    setTimeout(() => {
      getFeedbackList();
    }, 0);
  }, [getFeedbackList]);

  const handleExecutivePageChange = useCallback((event, value) => {
    setExecutiveCurrentPage(value);
    setTimeout(() => {
      getFeedbackList();
    }, 0);
  }, [getFeedbackList]);

  const handlePageChange = useCallback((event, value) => {
    if (activeTab === TAB_INDICES.HOSPITAL) {
      handleHospitalPageChange(event, value);
    } else {
      handleExecutivePageChange(event, value);
    }
  }, [activeTab, handleHospitalPageChange, handleExecutivePageChange]);

  const formatDateTime = useCallback((value) => {
    if (!value || value === "null" || value === "undefined") {
      return "-";
    }
    return value;
  }, []);

  useEffect(() => {
    getFeedbackList();
  }, [getFeedbackList, activeTab]);

  const columns = useMemo(() => {
    return activeTab === TAB_INDICES.HOSPITAL
      ? [
        { field: "id", header: "Enquiry ID" },
        { field: "hospitalName", header: "Hospital Name", width: "200px" },
        { field: "patientId", header: "Patient ID", width: "120px" },
        { field: "patientName", header: "Patient Name", width: "150px" },
        { field: "feedbackRate", header: "Rating", width: "150px" },
        { field: "feedbackNote", header: "Feedback Comments", width: "300px" },
        { field: "conciergeUserName", header: "Executive Name", width: "150px" }
      ]
      : [
        { field: "id", header: "Enquiry ID" },
        { field: "conciergeUserName", header: "Executive Name", width: "180px" },
        { field: "conciergeUserCode", header: "Executive Code", width: "120px" },
        { field: "hospitalName", header: "Hospital Name", width: "150px" },
        { field: "patientId", header: "Patient ID", width: "120px" },
        { field: "patientName", header: "Patient Name", width: "150px" },
        { field: "feedbackRate", header: "Rating", width: "150px" },
        { field: "feedbackNote", header: "Feedback Comments", width: "300px" },
      ];
  }, [activeTab]);

  const currentPage = useMemo(() => {
    return activeTab === TAB_INDICES.HOSPITAL ? hospitalCurrentPage : executiveCurrentPage;
  }, [activeTab, hospitalCurrentPage, executiveCurrentPage]);
  return (
    <OurLayouts>
      <Backdrop
        sx={{ color: "#FFFFFF", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress size={80} color="inherit" />
      </Backdrop>

      <div className="flex flex-col w-full mt-[4rem] overflow-auto">

        <div className="flex justify-between mt-[3rem]">
          <div>
            <h1 className="text-2xl text-primary font-semibold">
              Feedback Report
            </h1>
          </div>
        </div>

        <div className="flex items-center mt-1 gap-4 mr-10 p-4 bg-gray-50 rounded-lg">
          <div className="flex flex-col">
            <label htmlFor="fromDate" className="text-sm font-medium text-gray-700 mb-1">From Date</label>
            <Calendar
              id="fromDate"
              className="border border-gray-300 rounded-md p-2"
              placeholder="Select From Date"
              value={filterObj.fromDate}
              onChange={(e) => handleDateChange(e, "fromDate")}
              dateFormat="dd/mm/yy"
              style={{ height: "40px", width: "180px" }}
              maxDate={filterObj.toDate || new Date()}
              showIcon
            />
          </div>

          <div className="flex flex-col">
            <label htmlFor="toDate" className="text-sm font-medium text-gray-700 mb-1">To Date</label>
            <Calendar
              id="toDate"
              className="border border-gray-300 rounded-md p-2"
              placeholder="Select To Date"
              value={filterObj.toDate}
              onChange={(e) => handleDateChange(e, "toDate")}
              dateFormat="dd/mm/yy"
              style={{ height: "40px", width: "180px" }}
              minDate={filterObj.fromDate}
              maxDate={new Date()}
              showIcon
            />
          </div>

          <div className="flex flex-col">
            <label htmlFor="filterDropdown" className="text-sm font-medium text-gray-700 mb-1">
              {activeTab === TAB_INDICES.HOSPITAL ? "Hospital" : "Executive"}
            </label>
            {activeTab === TAB_INDICES.HOSPITAL ? (
              <Dropdown
                id="filterDropdown"
                placeholder="Select Hospital"
                value={filterObj.hospitalCode}
                options={hospitalList}
                optionLabel="label"
                optionValue="value"
                className="border border-gray-300 rounded-md"
                style={{ height: "40px", width: "220px" }}
                onChange={(e) => setFilterObj(prev => ({ ...prev, hospitalCode: e.value }))}
                filter
                showClear
              />
            ) : (
              <Dropdown
                id="filterDropdown"
                placeholder="Select Executive"
                value={filterObj.executiveCode}
                options={executiveList}
                optionLabel="label"
                optionValue="value"
                className="border border-gray-300 rounded-md"
                style={{ height: "40px", width: "220px" }}
                onChange={(e) => setFilterObj(prev => ({ ...prev, executiveCode: e.value }))}
                filter
                showClear
              />
            )}
          </div>

          <button
            id="searchButton"
            className="w-32 h-9 bg-[#004172] text-white mt-4 rounded-full hover:bg-[#00315a] transition-colors"
            onClick={handleSearch}
            type="button"
            aria-label="Search"
          >
            Search
          </button>
          <button
            id="refreshButton"
            className="w-32 h-9 bg-white text-[#004172] rounded-full mt-4 border border-[#004172] hover:bg-gray-50 transition-colors"
            onClick={handleReset}
            type="button"
            aria-label="Refresh"
          >
            Reset
          </button>
        </div>

        <div
          className="w-[99%] mt-4 rounded-[5px] bg-white"
          style={{ boxShadow: "0px 8px 25px 0px rgba(0, 0, 0, 0.06)" }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                fontWeight: 'bold',
                fontSize: '14px',
                textTransform: 'none',
                minWidth: '200px',
              }
            }}
          >
            <Tab label="Hospital Feedback Summary" />
            <Tab label="Executive Feedback Summary" />
          </Tabs>

          <TableContainer
            component={Paper}
            sx={{
              width: "100%",
              overflowX: "auto",
              boxShadow: 'none',
              '& .MuiTableCell-root': {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  {columns.map((column) => (
                    <TableCell
                      key={column.field}
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        minWidth: column.width,
                        maxWidth: column.width,
                        padding: "12px 16px",
                        borderBottom: "2px solid #e0e0e0"
                      }}
                    >
                      {column.header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {dataList.length > 0 ? (
                  dataList.map((row, index) => {
                    const rowKey = row.id || row.patientId || `${activeTab}_${index}`;
                    return (
                      <TableRow
                        key={rowKey}
                        sx={{
                          '&:hover': { backgroundColor: '#f5f5f5' },
                          '&:nth-of-type(odd)': { backgroundColor: '#fafafa' }
                        }}
                      >
                        {columns.map((column) => {
                          // Issue 9 fix: removed useless initial assignment, resolve displayValue directly
                          let displayValue;

                          if (column.field === "feedbackRate") {
                            displayValue = renderStars(row.feedbackRate);
                          } else if (column.field === "feedbackNote") {
                            displayValue = row.feedbackNote || "-";
                          } else {
                            displayValue = formatDateTime(row[column.field]) || "-";
                          }

                          return (
                            <TableCell
                              key={`${rowKey}-${column.field}`}
                              sx={{
                                fontSize: "13px",
                                fontWeight: 400,
                                minWidth: column.width,
                                maxWidth: column.width,
                                padding: "10px 16px",
                                borderBottom: "1px solid #e0e0e0"
                              }}
                              title={typeof displayValue === 'string' ? displayValue : ''}
                            >
                              <div className="truncate">
                                {displayValue}
                              </div>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      align="center"
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        padding: "40px 16px",
                        color: "#666"
                      }}
                    >
                      {loading ? "Loading feedback data..." : "No feedback available for the selected criteria"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalRecords > ITEMS_PER_PAGE && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                marginTop: "2rem",
                paddingBottom: "1rem",
              }}
            >
              <Pagination
                count={Math.ceil(totalRecords / ITEMS_PER_PAGE)}
                page={currentPage}
                color="primary"
                onChange={handlePageChange}
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </div>
      </div>
    </OurLayouts>
  );
};

export default FeedbackReport;